import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from '@bancobarigui/barihaus';

import { inspectorUserService } from '@/services/inspectorUser.service';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';
import { cleanPhoneNumber } from '@/shared/utils/phoneHelpers';
import { cleanDocument } from '@/utils/documentValidation';

export function useInspectorUserEdit() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();

  const loadUserData = async () => {
    if (!userId) {
      notify({
        type: 'error',
        message: 'ID do usuário não fornecido',
      });
      navigate('/inspector-list');
      return;
    }

    setLoading(true);
    try {
      const user = await inspectorUserService.getInspectorUserById(userId);

      form.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phoneNumber,
        cpf: user.cpf,
        integrationCode: user.integrationCode,
        postalCode: user.postalCode || '',
        street: user.street || '',
        number: user.number || '',
        complement: user.complement || '',
        district: user.district || '',
        state: user.state || '',
        city: user.city || '',
        allowsDirectAssignment: user.allowsDirectAssignment || false,
        allowsDemandReceiving: user.allowsDemandReceiving || false,
        providerIds: user.providerId ? [user.providerId] : [],
      });
    } catch (error) {
      handleError(error, 'Erro ao carregar dados do usuário');
      navigate('/inspector-list');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!userId) return;

    setSubmitting(true);
    try {
      const [providerId] = values.providerIds || [];

      const cleanPhone = cleanPhoneNumber(values.phone);

      await inspectorUserService.updateInspectorUser(userId, {
        email: values.email,
        cpf: cleanDocument(values.cpf),
        name: values.name,
        integrationCode: values.integrationCode,
        phoneNumber: cleanPhone,
        providerId: providerId || null,
        postalCode: values.postalCode,
        street: values.street,
        number: String(values.number),
        complement: values.complement || '',
        district: values.district,
        state: values.state,
        city: values.city,
        allowsDirectAssignment: values.allowsDirectAssignment || false,
        allowsDemandReceiving: values.allowsDemandReceiving || false,
      });

      notify({
        type: 'success',
        message: 'Usuário atualizado com sucesso!',
      });
      navigate('/inspector-list');
    } catch (error) {
      handleError(error, 'Erro ao atualizar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/inspector-list');
  };

  useEffect(() => {
    loadUserData();
  }, [userId]);

  return {
    form,
    loading,
    submitting,
    handleSubmit,
    handleCancel,
  };
}
